import { FunctionComponent, memo } from "react";
import createPersistedState from "use-persisted-state";
import MetricBox from "components-for/display/metric-box";
import { faTag } from "@fortawesome/pro-solid-svg-icons";
import useTags from "hooks-for/tags/use-tags";
import { useInteractionQueryParameters } from "hooks-for/filters/use-interaction-query-parameters";
import { useQueryParameters } from "@edgetier/client-components";
import useLookupQueryTags from "hooks-for/tags/use-lookup-query-tags";
import { parseUrlArray } from "@edgetier/utilities";
import { calculatePercentageValue } from "utilities-for/formatting/calculate-percentage-value";
import { useInteractionsCount } from "hooks-for/interactions/use-interactions-counts";
import ChooseTagAction from "./choose-tag-action";
import { IProps } from "./agent-tag-metric.types";
import { PersistedStorageKeys } from "constants/persisted-storage-keys";
import { isValueEmpty } from "utilities/is-value-empty";
import "./agent-tag-metric.scss";

const useTagSelected = createPersistedState<string[]>(PersistedStorageKeys.TagSelected);

/**
 * Show the percentage of interactions the agent has completed of a tag versus all agents overall.
 * @param props.defaultEndDateTime   Closing date in range to search.
 * @param props.defaultStartDateTime Opening date in range to search.
 */
const AgentTagMetric: FunctionComponent<IProps> = ({ defaultEndDateTime, defaultStartDateTime }) => {
    const { data: tags, isLoading: areTagsLoading } = useTags();
    const [tagSelected] = useTagSelected([]);
    const selectedTagDetails = (tags ?? []).find((tag) => tag.tagId === tagSelected[0]);

    // Get all filters from query paramters
    const filters = useInteractionQueryParameters({
        defaults: { startDateTime: defaultStartDateTime, endDateTime: defaultEndDateTime },
    });

    // Select agentId and any other agents selected for comparison
    const { comparisonAgentId, reviewAgentId } = useQueryParameters({
        comparisonAgentId: parseUrlArray,
        reviewAgentId: String,
    });

    // Interactions count for selected agent
    const { data: agentCount, isLoading: isAgentInteractionsCountLoading } = useInteractionsCount({
        ...filters,
        agentId: reviewAgentId,
    });

    // Interactions count for comparison agent(s)
    const { data: comparisonAgentCount, isLoading: isComparisonAgentInteractionsCountLoading } = useInteractionsCount({
        ...filters,
        agentId: comparisonAgentId,
    });

    const lookupQueryTags = useLookupQueryTags(filters.tagId);
    // Agent's interactions count with selected tags
    const { data: agentTagCounts, isLoading: isAgentTagCountsLoading } = useInteractionsCount(
        {
            ...filters,
            agentId: reviewAgentId,
            tagId: lookupQueryTags[tagSelected[0]],
        },
        { enabled: !isValueEmpty(tagSelected[0]) }
    );

    // interactions count with selected tags for comparison agent(s)
    const { data: comparisonAgentTagCounts, isLoading: isComparisonAgentTagCountsLoading } = useInteractionsCount(
        { ...filters, tagId: lookupQueryTags[tagSelected[0]], agentId: comparisonAgentId },
        {
            enabled: !isValueEmpty(tagSelected[0]),
        }
    );

    const agentTagValue = calculatePercentageValue(agentCount, agentTagCounts);
    const comparisonAgentTagValue = calculatePercentageValue(comparisonAgentCount, comparisonAgentTagCounts);

    return (
        <MetricBox
            icon={faTag}
            isLoading={
                areTagsLoading ||
                isAgentInteractionsCountLoading ||
                isComparisonAgentInteractionsCountLoading ||
                isAgentTagCountsLoading ||
                isComparisonAgentTagCountsLoading
            }
            title={`${typeof selectedTagDetails !== "undefined" ? selectedTagDetails.tag : "Choose tag"}`}
            tooltipContent={`${
                typeof selectedTagDetails !== "undefined"
                    ? "Percentage of interactions the agent has completed with this tag"
                    : "Choose tag"
            }`}
            unit="%"
            options={{ maximumFractionDigits: 0 }}
            comparisonValue={comparisonAgentTagValue ?? null}
            value={agentTagValue ?? null}
            menuOptions={
                <>
                    <ChooseTagAction />
                </>
            }
        />
    );
};

export default memo(AgentTagMetric);
